import celfinetLogo  from '../../../../assets/Cyient_Slate_Std_RGB.png'
import { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, Dialog, DialogContent, IconButton, Slider, Tooltip, Typography } from '@mui/material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import CloseIcon      from '@mui/icons-material/Close'
import DownloadIcon   from '@mui/icons-material/Download'
import TuneIcon       from '@mui/icons-material/Tune'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Line } from 'react-chartjs-2'
import { selectBeacon4VisibleSeries } from '../../reducers/Beacon4FiltersReducer'

const TECH_COL = {
  '2G': { border: '#1a3a6e', bg: 'rgba(26,58,110,.18)'   },
  '3G': { border: '#4fc3f7', bg: 'rgba(79,195,247,.18)'  },
  '4G': { border: '#f57c00', bg: 'rgba(245,124,0,.18)'   },
  '5G': { border: '#ffb74d', bg: 'rgba(255,183,77,.18)'  },
}

/**
 * Small "↻" icon button that resets one slider to its default range [0,100].
 */
function ResetButton({ onClick, disabled, title = 'Reset to default range' }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small" onClick={onClick} disabled={disabled}
          sx={{ p: 0.25, ml: 0.25, color: disabled ? 'text.disabled' : 'text.secondary',
                '&:hover': { color: 'primary.main' } }}
        >
          <RestartAltIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function buildDatasets(seriesList, visibleSeries = null) {
  return seriesList.map(t => {
    const col = TECH_COL[t.key] ?? { border: '#888', bg: 'rgba(128,128,128,.15)' }
    return {
      label:           t.key,
      data:            [...(t.data ?? [])],
      borderColor:     col.border,
      backgroundColor: col.bg,
      borderWidth:     1.5,
      pointRadius:     0,
      lineTension:     0,
      fill:            true,
      hidden:          visibleSeries !== null && !visibleSeries.includes(t.key),
    }
  })
}

function buildOptions(unit, maxTicksLimit = 9, pinnedActive = false, yBounds = null) {
  return {
    responsive:          true,
    maintainAspectRatio: false,
    legend:              { display: false },
    tooltips: {
      enabled:         !pinnedActive,
      mode:            'index',
      intersect:       false,
      position:        'nearest',
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor:     'rgba(0,0,0,0.1)',
      borderWidth:     1,
      titleFontColor:  '#8fa3bc',
      titleFontSize:   10,
      bodyFontColor:   '#1a2233',
      bodyFontSize:    11,
      xPadding:        10,
      yPadding:        10,
      cornerRadius:    6,
      callbacks: {
        label: (item, data) =>
          ` ${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toFixed(1)} ${unit}`,
        labelColor: (item, chart) => {
          const ds = chart.data.datasets[item.datasetIndex]
          return { borderColor: ds.borderColor, backgroundColor: ds.borderColor }
        },
      },
    },
    scales: {
      xAxes: [{ ticks: { fontSize: 8, maxRotation: 45, autoSkip: true, maxTicksLimit }, gridLines: { display: false }, stacked: true }],
      yAxes: [{
        ticks: {
          fontSize: 8, maxTicksLimit: 5, callback: v => v,
          ...(yBounds?.min != null ? { min: yBounds.min } : {}),
          ...(yBounds?.max != null ? { max: yBounds.max } : {}),
        },
        gridLines: { color: 'rgba(0,0,0,.05)' },
        stacked:   true,
      }],
    },
  }
}

function TrafficLegend({ seriesList, visibleSeries }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 90, pl: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        Technologies
      </Typography>
      {seriesList.map(t => {
        const col      = TECH_COL[t.key] ?? { border: '#888', bg: 'rgba(128,128,128,.15)' }
        const isHidden = visibleSeries !== null && !visibleSeries.includes(t.key)
        return (
          <Box key={t.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, opacity: isHidden ? 0.35 : 1 }}>
            <Box sx={{ width: 16, height: 12, borderRadius: '3px', bgcolor: col.bg, border: `1.5px solid ${col.border}`, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: 'text.primary' }}>{t.key}</Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export default function Beacon4StackedTrafficChart({ title, labels = [], seriesList = [], height = 220, unit = 'erl' }) {
  const fillHeight   = height === '100%'
  const visibleSeries = useSelector(selectBeacon4VisibleSeries)
  const [open,          setOpen]         = useState(false)
  const [range,         setRange]        = useState([0, 100])
  const [yRange,        setYRange]       = useState([0, 100])  // % of the data's stacked min/max
  const [sliders,       setSliders]      = useState(false)
  const [pinnedTooltip,      setPinnedTooltip]      = useState(null)
  const [modalPinnedTooltip, setModalPinnedTooltip] = useState(null)
  const modalChartRef    = useRef(null)
  const modalChartBoxRef = useRef(null)
  const modalLineRef     = useRef(null)
  const chartRef         = useRef(null)
  const chartBoxRef      = useRef(null)

  // Reset helpers — each slider has its own "↻", plus a "Reset all" header.
  const isXReset = range[0]  === 0 && range[1]  === 100
  const isYReset = yRange[0] === 0 && yRange[1] === 100
  const allReset = isXReset && isYReset
  const resetX   = () => { setRange([0, 100]); setPinnedTooltip(null); setModalPinnedTooltip(null) }
  const resetY   = () => setYRange([0, 100])
  const resetAll = () => { resetX(); resetY() }

  // Compute stacked Y bounds across the currently visible (X-sliced + legend-
  // filtered) data, then derive the chart's y-axis min/max from the yRange
  // slider's percent values. Memoised so we only recompute when the inputs
  // change, not on every render. Must be defined BEFORE any early return so
  // the hook order stays stable.
  const { yDataMin, yDataMax } = useMemo(() => {
    if (!labels?.length || !seriesList?.length) return { yDataMin: 0, yDataMax: 0 }
    const total = labels.length
    const s = Math.floor(range[0] / 100 * total)
    const e = Math.ceil(range[1]  / 100 * total)
    let stackMax = 0
    for (let i = s; i < e; i++) {
      let sum = 0
      for (const t of seriesList) {
        if (visibleSeries !== null && !visibleSeries.includes(t.key)) continue
        const v = (t.data ?? [])[i]
        if (typeof v === 'number' && isFinite(v)) sum += v
      }
      if (sum > stackMax) stackMax = sum
    }
    return { yDataMin: 0, yDataMax: stackMax }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, seriesList, visibleSeries, range[0], range[1]])

  const exportChartPDF = async () => {
    const el = modalChartRef.current
    if (!el) return
    const html2canvas = (await import('html2canvas')).default
    const { jsPDF }   = await import('jspdf')
    const canvas = await html2canvas(el, { scale: 3, backgroundColor: '#ffffff', logging: false })
    const w = canvas.width / 2, h = canvas.height / 2
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] })
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h)
    pdf.save(`${title.replace(/[^a-z0-9]/gi, '_')}.pdf`)
  }

  if (!seriesList.length || !labels.length) {
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', mb: 1 }}>{title}</Typography>
        <Box sx={{ height, bgcolor: '#f7f8fa', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 11, color: '#8fa3bc' }}>Loading…</Typography>
        </Box>
      </Box>
    )
  }

  const safeLabels     = [...labels]
  const totalPts       = safeLabels.length
  const startIdx       = Math.floor(range[0] / 100 * totalPts)
  const endIdx         = Math.ceil(range[1]  / 100 * totalPts)
  const slicedLabels   = safeLabels.slice(startIdx, endIdx)
  const datasets       = buildDatasets(seriesList, visibleSeries)
  const slicedDatasets = datasets.map(d => ({ ...d, data: (d.data ?? []).slice(startIdx, endIdx) }))

  // Map [0..100] slider values to actual data-domain min/max. Slider at the
  // default [0, 100] = "auto" (Chart.js picks bounds itself) — we leave the
  // ticks min/max undefined in that case so default behaviour is preserved.
  const yBounds = sliders && !isYReset
    ? {
        min: yDataMin + (yRange[0] / 100) * (yDataMax - yDataMin),
        max: yDataMin + (yRange[1] / 100) * (yDataMax - yDataMin),
      }
    : null

  return (
    <>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.04)', ...(fillHeight && { height: '100%', display: 'flex', flexDirection: 'column' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
          <Typography onClick={() => setOpen(true)} sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
            {title}
          </Typography>
          <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 0.25 }}>
            <IconButton size="small" onClick={() => setSliders(v => !v)}
              sx={{ p: 0.25, color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <TuneIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <FullscreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          ref={chartBoxRef}
          onClick={e => {
            const chart = chartRef.current?.chartInstance
            if (!chart || !chartBoxRef.current) return
            const rect  = chartBoxRef.current.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const clickY = e.clientY - rect.top
            const pts   = chart.getElementsAtXAxis(e.nativeEvent)
            if (!pts?.length) {
              const ci = chartRef.current?.chartInstance
              if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              setPinnedTooltip(null)
              return
            }
            const idx   = pts[0]._index
            const label = chart.data.labels[idx]
            const ptX   = pts[0]._view?.x ?? clickX
            const ptY   = pts[0]._view?.y ?? clickY
            const items = chart.data.datasets
              .filter(ds => !ds.hidden)
              .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
              .filter(it => it.value != null)
            const ci = chartRef.current?.chartInstance
            if (ci) { ci.options.tooltips.enabled = false; ci.update(0) }
            setPinnedTooltip({ label, items, x: ptX, y: ptY })
          }}
          sx={{ position: 'relative', ...(fillHeight ? { flex: 1, minHeight: 0 } : { height }) }}>
          {/* Watermark */}
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
            <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
          </Box>
          <Line ref={chartRef} data={{ labels: slicedLabels, datasets: slicedDatasets }} options={buildOptions(unit, 9, !!pinnedTooltip, yBounds)} />
          {pinnedTooltip && (
            <Box sx={{
              position: 'absolute',
              left: pinnedTooltip.x + 8,
              top:  Math.max(pinnedTooltip.y - 20, 0),
              transform: pinnedTooltip.x > 150 ? 'translateX(-100%)' : 'none',
              zIndex: 10, pointerEvents: 'auto',
              bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
              minWidth: 130,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>
                  {pinnedTooltip.label}
                </Typography>
                <Box onClick={e => {
                  e.stopPropagation()
                  const ci = chartRef.current?.chartInstance
                  if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                  setPinnedTooltip(null)
                }}
                  sx={{ fontSize: 11, cursor: 'pointer', color: 'text.disabled', ml: 1, lineHeight: 1, '&:hover': { color: 'text.primary' } }}>
                  ✕
                </Box>
              </Box>
              {pinnedTooltip.items.map(it => (
                <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '1px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1 }}>{it.key}</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.primary' }}>
                    {Number(it.value).toFixed(1)} {unit}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {sliders && totalPts > 0 && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
              <Button
                size="small" onClick={resetAll} disabled={allReset}
                startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                sx={{ py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                      color: allReset ? 'text.disabled' : 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}>
                Reset all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                  <ResetButton onClick={resetX} disabled={isXReset} />
                </Box>
                <Slider value={range} onChange={(_, v) => setRange(v)} size="small" disableSwap
                  sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({unit})</Typography>
                  <ResetButton onClick={resetY} disabled={isYReset} />
                </Box>
                <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(yDataMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(yDataMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: 2 } }}>
        <DialogContent sx={{ p: '20px 24px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2d3748' }}>{title}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Toggle sliders">
                <IconButton size="small" onClick={() => setSliders(v => !v)}
                  sx={{ color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export chart as PDF">
                <IconButton size="small" onClick={exportChartPDF} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => {
                setOpen(false)
                setModalPinnedTooltip(null)
                const ci = modalLineRef.current?.chartInstance
                if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              }} sx={{ color: 'text.disabled' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
            {sliders && (
              <Box sx={{ px: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Button
                    size="small" onClick={resetAll} disabled={allReset}
                    startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                    sx={{ py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                          color: allReset ? 'text.disabled' : 'text.secondary',
                          '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}>
                    Reset all
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                      <ResetButton onClick={resetX} disabled={isXReset} />
                    </Box>
                    <Slider value={range} onChange={(_, v) => setRange(v)} size="small" disableSwap
                      sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({unit})</Typography>
                      <ResetButton onClick={resetY} disabled={isYReset} />
                    </Box>
                    <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(yDataMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(yDataMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              <Box ref={modalChartRef} sx={{ flex: 1, position: 'relative' }}>
                {/* Watermark */}
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                  <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
                </Box>
                <Line ref={modalLineRef} data={{ labels: slicedLabels, datasets: slicedDatasets }} options={buildOptions(unit, 16, !!modalPinnedTooltip, yBounds)} />
              </Box>
              <TrafficLegend seriesList={seriesList} visibleSeries={visibleSeries} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}